//src/pages/internal/cadastroHosts/index.js
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import useStyles from "./styles";
import { useTheme } from "@emotion/react";
import { Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../../services/api";
import LoaderThreeDots from "../../../components/loadingAnimation";
import {
  Add,
  Cached,
  Clear,
  DeleteOutline,
  Edit,
  FilterAltOutlined,
  Search,
} from "@mui/icons-material";
import FilterBar from "../../../components/topBarFilter";
import CreateHostModal from "./cadastro/modalNovoHost";
import EditHostModal from "./cadastro/modalEditarHost"; // <--- Importar Modal de Edição
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";

export default function CadastroHosts() {
  const [pageValue, setPageValue] = useState("cadastrados");
  const [hostsCadastrados, setHostsCadastrados] = useState([]);
  const [hostsPrecadastrados, setHostsPrecadastrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [snackbar, setSnackbar] = useState(null);
  const [buscarHost, setBuscarHost] = useState("");

  // --- Estados para os Modais ---
  const [openCreateHostModal, setOpenCreateHostModal] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [confirmLoad, setConfirmLoad] = useState(false);
  const [hostToDelete, setHostToDelete] = useState(null);

  // Estado do mapa hostId → associações de clientes/serviços
  const [hostClientsMap, setHostClientsMap] = useState({});

  // Novos estados para Edição
  const [openEditHostModal, setOpenEditHostModal] = useState(false);
  const [editingHostData, setEditingHostData] = useState(null);
  const [proxys, setProxys] = useState([]);

  // Presets de ping (ícones) e os destinos padrão (slots A–E)
  const [pingIcons, setPingIcons] = useState([]);
  const [pingDefaults, setPingDefaults] = useState([]);

  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = () => {
    apiGetHostsCadastrados();
    apiGetTemplates();
    apitGetProxys();
    fetchPingPresets();
    apiGetHostClients();
  };

  // Busca os ícones de ping (presets) com miniaturas e os destinos padrão (A–E)
  async function fetchPingPresets() {
    try {
      const response = await api.post("/anexos/findMany", {
        type: "ping_icon",
      });
      const data = response?.data || [];

      const withPreview = await Promise.all(
        data.map(async (icon) => {
          try {
            const imgRes = await api.post(
              "/anexos/download",
              { id: icon.id },
              { responseType: "arraybuffer" },
            );
            const blob = new Blob([imgRes.data], { type: icon.mimeType });
            return { ...icon, imageUrl: URL.createObjectURL(blob) };
          } catch (e) {
            return { ...icon, imageUrl: null };
          }
        }),
      );
      setPingIcons(withPreview);

      const settings = await api.post("/interno/find/generalSetting", {
        product: "pingPresets",
      });
      setPingDefaults(
        Array.isArray(settings?.data?.defaults) ? settings.data.defaults : [],
      );
    } catch (error) {
      console.error("Erro ao buscar presets de ping:", error);
    }
  }

  async function apiGetHostsCadastrados() {
    setLoading(true);
    try {
      const response = await api.get("/zabbix/hosts/findMany");
      if (response?.data?.hosts) {
        setHostsCadastrados(response?.data?.hosts);
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children:
          error?.response?.data?.message || "Erro ao buscar hosts cadastrados",
      });
    } finally {
      setLoading(false);
    }
  }

  // Busca o mapa de clientes/serviços associados a cada host
  async function apiGetHostClients() {
    try {
      const response = await api.post("/interno/find/hostsClientsServices");
      setHostClientsMap(response?.data || {});
    } catch (error) {
      console.error("Erro ao buscar clientes por host:", error);
    }
  }

  async function apiGetTemplates() {
    try {
      const response = await api.get("/getTemplates");
      setTemplates(response?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: error?.response?.data?.message || "Erro ao buscar templates",
      });
    }
  }

  async function apitGetProxys(params) {
    try {
      const reponse = await api.get("/zabbix/hosts/proxys", { params });
      setProxys(reponse?.data);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Error:${error?.response?.data?.error || "Ao buscar os proxys"} `,
        severity: "error",
      });
    }
  }

  async function createHost(host) {
    try {
      setLoadingCreate(true);
      const response = await api.post("/zabbix/hosts/create", { host: host });
      const hostid = response?.data?.hostid;
      const groupId = response?.data?.groupId || null;
      host.id = hostid;
      let newHosts = [...hostsCadastrados];
      host.checks = {
        ping: null,
        snmp: null,
        ssh: null,
      };
      newHosts.push(host);
      setHostsCadastrados(newHosts);

      setSnackbar({
        severity: "success",
        children: "Host criado com sucesso.",
      });
      return { hostid, groupId };
    } catch (error) {
      console.error(error);

      setSnackbar({
        severity: "error",
        children:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao criar host.",
      });
    } finally {
      setLoadingCreate(false);
    }
  }

  async function updateHost(host) {
    try {
      setLoadingCreate(true);
      // Ajuste a rota conforme sua API (ex: update, updateOne, etc)
      await api.put("/zabbix/hosts/update", { host });

      let newHosts = [...hostsCadastrados];
      const index = newHosts.findIndex((h) => h.id === host.id);
      if (index !== -1) {
        newHosts[index] = host;
        newHosts[index].checks = {
          ping: null,
          snmp: null,
          ssh: null,
        };
      }
      setHostsCadastrados(newHosts);

      setSnackbar({
        severity: "success",
        children: "Host atualizado com sucesso.",
      });
      return true;
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Erro ao atualizar host.",
      });
    } finally {
      setLoadingCreate(false);
    }
  }

  const handleRequestDelete = (hostId) => {
    setHostToDelete(hostId);
    setOpenModalConfirmacao(true);
  };

  // Função executada pelo Modal ao confirmar
  async function deleteHost() {
    //console.log(hostToDelete);
    if (!hostToDelete) return;

    try {
      setConfirmLoad(true); // Ativa o loading do modal
      await api.post(`/zabbix/hosts/delete`, { hostId: hostToDelete });

      // Atualiza a lista localmente para ser mais rápido visualmente
      let newHosts = [...hostsCadastrados];
      // Verifique se o seu backend retorna 'hostId' ou 'id'. O filtro abaixo tenta os dois.
      newHosts = newHosts.filter(
        (host) => (host.hostId || host.id) !== hostToDelete,
      );
      setHostsCadastrados(newHosts);

      setSnackbar({
        severity: "success",
        children: "Host excluído com sucesso.",
      });
      setOpenModalConfirmacao(false); // Fecha o modal apenas se deu certo
      setHostToDelete(null); // Limpa o ID
    } catch (error) {
      console.error(error);
      setSnackbar({
        severity: "error",
        children: "Erro ao excluir host.",
      });
    } finally {
      setConfirmLoad(false);
    }
  }

  // ... (restante dos stylesChips e actions mantidos) ...
  const stylesChips = (severity) => ({
    backgroundColor: alpha(theme.palette[severity].main, 0.2),
    color:
      theme.palette.mode === "dark"
        ? theme.palette[severity].light
        : theme.palette[severity].dark,
    fontWeight: "bold",
    "&.MuiChip-root": { borderRadius: 1 },
    "&.MuiChip-root .MuiChip-label": { marginTop: "2px" },
  });

  const actions = [
    {
      icon: <FilterAltOutlined />,
      name: "Filtro",
      onClick: () => setOpenFilter(true),
    },
  ];
  const pathpage = window.location.pathname;

  return (
    <Box sx={styles.container}>
      <FilterBar
        actions={actions}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        type={0}
        selectorComponentRight={
          <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
            {/* ... (Botões da FilterBar mantidos iguais) ... */}
            {pathpage === "/interno/cadastroHosts/cadastrados" && (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  gap: 1,
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <TextField
                  id={"host-search"}
                  size="small"
                  label="Buscar Host"
                  variant="outlined"
                  onChange={(event) => setBuscarHost(event.target?.value)}
                  sx={styles.textField}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: buscarHost ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setBuscarHost("")}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    sx={{
                      color: theme.palette.text.main,
                      borderColor: theme.palette.text.main,
                    }}
                    onClick={() => setOpenCreateHostModal(true)}
                    disabled={loading}
                  >
                    Novo Host
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loading}
                    onClick={() => fetchAllData()}
                    sx={{
                      ...styles.buttonRefresh,
                      "& .rotating": {
                        animation: "spin 1s linear infinite",
                      },
                      "@keyframes spin": {
                        "0%": {
                          transform: "rotate(0deg)",
                        },
                        "100%": {
                          transform: "rotate(-360deg)",
                        },
                      },
                    }}
                  >
                    <Cached
                      fontSize="small"
                      sx={{ color: theme.palette.text.main }}
                      className={loading ? "rotating" : ""}
                    />
                  </Button>
                </Box>
              </Box>
            )}
            {/* ... (Botões da aba precadastro mantidos) ... */}
            {pathpage === "/interno/cadastroHosts/precadastro" && (
              <Box>
                <Button
                  startIcon={<Add />}
                  variant="outlined"
                  size="small"
                  disabled={!isEditable}
                >
                  Novo Host
                </Button>
                <Button
                  startIcon={<Edit />}
                  variant="outlined"
                  size="small"
                  onClick={() => setIsEditable(!isEditable)}
                >
                  {isEditable ? "Finalizar Edição" : "Habilitar Edição"}
                </Button>
                <Button
                  startIcon={<DeleteOutline />}
                  color="error"
                  variant="outlined"
                  size="small"
                >
                  Excluir
                </Button>
                <Button
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  size="small"
                >
                  Adicionar Hosts
                </Button>
              </Box>
            )}
          </Box>
        }
      />

      <Tabs
        textColor="inherit"
        onChange={(event, newValue) => {
          navigate(`/interno/cadastroHosts/${newValue}`);
          setPageValue(newValue);
        }}
        sx={styles.tabs}
        value={pageValue}
      >
        <Tab
          label={
            <Box sx={styles.boxTab}>
              Equipamentos Cadastrados
              <Chip
                label={hostsCadastrados?.length || 0}
                size="small"
                sx={stylesChips("success")}
              />
            </Box>
          }
          value={"cadastrados"}
          sx={{ p: 0 }}
        />
      </Tabs>

      {loading ? (
        <Box sx={styles.boxLoading}>
          <LoaderThreeDots />
        </Box>
      ) : (
        <Outlet
          context={{
            hostsCadastrados,
            hostsPrecadastrados,
            setHostsPrecadastrados,
            setIsEditable,
            templates,
            setOpenEditHostModal,
            setEditingHostData,
            handleRequestDelete,
            proxys,
            buscarHost,
            hostClientsMap,
            setHostClientsMap,
          }}
        />
      )}

      {/* Modal de Criação */}
      <CreateHostModal
        open={openCreateHostModal}
        onClose={() => setOpenCreateHostModal(false)}
        onSave={createHost}
        templates={templates}
        loading={loadingCreate}
        proxys={proxys}
        pingIcons={pingIcons}
        pingDefaults={pingDefaults}
        hostClientsMap={hostClientsMap}
        setHostClientsMap={setHostClientsMap}
      />

      {/* Modal de Edição */}
      <EditHostModal
        open={openEditHostModal}
        hostData={editingHostData} // Passa os dados da linha selecionada
        onClose={() => {
          setOpenEditHostModal(false);
          setEditingHostData(null);
        }}
        onSave={updateHost}
        templates={templates}
        loading={loadingCreate}
        proxys={proxys}
        pingIcons={pingIcons}
        pingDefaults={pingDefaults}
        hostClientsMap={hostClientsMap}
        setHostClientsMap={setHostClientsMap}
      />

      <InternoModalConfirmacaoSaida
        openConfirmacao={openModalConfirmacao}
        setOpenConfirmacao={() => setOpenModalConfirmacao(false)}
        confirmLoad={confirmLoad}
        execute={deleteHost}
        severity="error"
        contentText="Tem certeza que deseja excluir o host?"
        confirmText="Excluir"
        title="Excluir Host"
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
